import React from 'react';
import { Plus, X, Edit2, Eye } from 'lucide-react';
import { useData } from '../providers/DataProvider';
import { computeOrderCashNeeded } from '../lib/orderCharges';
import { validateBranch } from '../lib/validators';

const Branches = ({ branches = [], transactions = [], currentUser = null, onAddBranch, onUpdateBranch, onTransferBetweenBranches }) => {
  // Prefer balances coming from DB view when available
  const data = (typeof useData === 'function') ? useData() : null;
  const branchBalances = (data && Array.isArray(data.branchBalances)) ? data.branchBalances : [];
  const branchMovements = (data && Array.isArray(data.branchMovements)) ? data.branchMovements : [];

  // Fallback computation from transactions when DB view is missing or empty
  const getBranchKey = (item) => String(item?.branchId ?? item?.branch_id ?? '');
  const getBranchNameById = (branchId) => {
    const key = String(branchId ?? '');
    if (!key) return '';
    const match = (Array.isArray(branches) ? branches : []).find((b) => String(b?.id) === key);
    return match?.name || key;
  };

  const getBranchBalanceRow = (branchId) => {
    if (!branchId) return null;
    const row = branchBalances.find((r) => String(r.branch_id) === String(branchId));
    return row || null;
  };
  const computeMovementTotalsForBranch = (branchId) => {
    try {
      const ms = Array.isArray(branchMovements) ? branchMovements : [];
      const filtered = ms.filter((m) => String(m.branch_id) === String(branchId));
      const entradas = filtered.reduce((sum, m) => sum + (m.movement_type === 'entrada' ? Number(m.amount || 0) : 0), 0);
      const salidas = filtered.reduce((sum, m) => sum + (m.movement_type === 'salida' ? Number(m.amount || 0) : 0), 0);
      return { entradas, salidas };
    } catch {
      return { entradas: 0, salidas: 0 };
    }
  };

  const getBranchCash = (branch) => {
    // Use pre-computed balance from v_branch_cash_computed view
    // This is calculated directly from transactions, so it's always accurate
    const row = getBranchBalanceRow(branch?.id);
    if (row && row.cash_balance !== undefined) {
      return Number(row.cash_balance) || 0;
    }

    // Fallback to movements calculation if view data not available
    const mov = computeMovementTotalsForBranch(branch?.id);
    return mov.entradas - mov.salidas;
  };
  // Si no es admin, mostrar solo su sucursal (manejar currentUser nulo)
  const role = (currentUser?.role ?? '').toString().toLowerCase();
  const isAdmin = role === 'admin' || role.includes('admin');
  const visibleBranches = isAdmin
    ? (branches || [])
    : (branches || []).filter(b => String(b?.id) === String(currentUser?.branchId ?? ''));

  const [showModal, setShowModal] = React.useState(false);
  const [newBranch, setNewBranch] = React.useState({ name: '', address: '', cashBalance: 0 });
  const [details, setDetails] = React.useState({ show: false, branch: null });
  const [editModal, setEditModal] = React.useState({ show: false, branch: null });
  const newErrors = React.useMemo(() => validateBranch(newBranch), [newBranch]);
  const editErrors = React.useMemo(() => validateBranch(editModal?.branch || {}), [editModal?.branch]);
  // Internal branch transfers removed

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewBranch(prev => ({ ...prev, [name]: name === 'cashBalance' ? Number(value || 0) : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (Array.isArray(newErrors) && newErrors.length > 0) return;
    if (onAddBranch) {
      onAddBranch({ ...newBranch });
    }
    setShowModal(false);
    setNewBranch({ name: '', address: '', cashBalance: 0 });
  };

  const handleViewDetails = (branch) => {
    setDetails({ show: true, branch });
  };

  const handleEditBranch = (branchId) => {
    const branch = (branches || []).find(b => b.id === branchId);
    if (branch) {
      setEditModal({ show: true, branch: { ...branch } });
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditModal(prev => ({
      ...prev,
      branch: { ...prev.branch, [name]: name === 'cashBalance' ? Number(value || 0) : value }
    }));
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (Array.isArray(editErrors) && editErrors.length > 0) return;
    if (typeof onUpdateBranch === 'function' && editModal.branch) {
      onUpdateBranch({ ...editModal.branch });
    } else {
      console.debug('[Branches] Updated (local only):', editModal.branch);
    }
    setEditModal({ show: false, branch: null });
  };

  // openTransfer and submitTransfer removed

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Sucursales</h1>
        {isAdmin && (
          <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Sucursal
          </button>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md neon-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nueva Sucursal</h3>
              <button onClick={() => setShowModal(false)} className="px-2 py-1 text-gray-600 dark:text-gray-300">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Nombre</label>
                <input name="name" value={newBranch.name} onChange={handleChange} required className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Dirección</label>
                <input name="address" value={newBranch.address} onChange={handleChange} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Efectivo inicial</label>
                <input type="number" name="cashBalance" value={newBranch.cashBalance} onChange={handleChange} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              {Array.isArray(newErrors) && newErrors.length > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300">{newErrors[0]}</div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="border px-3 py-1 rounded border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700">Cancelar</button>
                <button type="submit" disabled={Array.isArray(newErrors) && newErrors.length > 0} className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleBranches.length === 0 && (
          <div className="col-span-full text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            {isAdmin ? 'No hay sucursales para mostrar. ¡Agrega una nueva!' : 'No hay sucursales disponibles para tu usuario.'}
          </div>
        )}
        {visibleBranches.map((branch) => (
          <div key={branch.id} className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{branch.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{branch.address}</p>
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Efectivo disponible:</span>
                <span className="text-sm font-medium text-gray-800 dark:text-white">${getBranchCash(branch).toLocaleString('en-US')}</span>
              </div>
              {(() => {
                const row = getBranchBalanceRow(branch?.id);
                if (row) {
                  const entradas = Number(row.total_entradas ?? 0) || 0;
                  const salidas = Number(row.total_salidas ?? 0) || 0;
                  return (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                      <span>Entradas: ${entradas.toLocaleString('en-US')}</span>
                      <span>Salidas: ${salidas.toLocaleString('en-US')}</span>
                    </div>
                  );
                }
                // Use movements only (transactions are tracked via triggers)
                const movTotals = computeMovementTotalsForBranch(branch?.id);
                const entradas = Number(movTotals.entradas || 0);
                const salidas = Number(movTotals.salidas || 0);
                return (
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                    <span>Entradas: ${entradas.toLocaleString('en-US')}</span>
                    <span>Salidas: ${salidas.toLocaleString('en-US')}</span>
                  </div>
                );
              })()}
              
              <div className="mt-4 flex space-x-2">
                <button onClick={() => handleViewDetails(branch)} className="flex-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center">
                  <Eye className="w-4 h-4 mr-1" />
                  Ver detalles
                </button>
                {isAdmin && (
                  <>
                    <button onClick={() => handleEditBranch(branch.id)} className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center">
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal: Ver detalles con actividad */}
      {details?.show && details.branch && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl relative grid grid-cols-1 md:grid-cols-2 gap-6 neon-border">
            <button onClick={() => setDetails({ show: false, branch: null })} className="absolute top-3 right-3 p-2 rounded hover:bg-gray-100 hover:dark:bg-gray-700">
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <h3 className="md:col-span-2 text-xl font-semibold text-gray-900 dark:text-white mb-2">Detalles de la Sucursal</h3>
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Nombre</div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{details.branch.name}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Dirección</div>
                <div className="text-sm text-gray-900 dark:text-gray-100">{details.branch.address || '—'}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Efectivo disponible</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">${getBranchCash(details.branch).toLocaleString('en-US')}</div>
              </div>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Movimientos de caja</h4>
              {branchMovements.filter(m => String(m.branch_id) === String(details.branch.id)).slice(0, 50).map((m, idx) => (
                <div key={`${m.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.movement_type === 'salida' ? 'Salida' : 'Entrada'} • {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-300">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</div>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-200">Monto: ${Number(m.amount || 0).toLocaleString('en-US')}</div>
                  {m.description && <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">{m.description}</div>}
                </div>
              ))}
              {branchMovements.filter(m => String(m.branch_id) === String(details.branch.id)).length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">Sin movimientos de caja para esta sucursal.</div>
              )}
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-4">Operaciones (pedidos/ventas/transferencias)</h4>
              {(Array.isArray(transactions) ? transactions : [])
                .filter((t) => {
                  const branchKey = String(details.branch.id);
                  const type = (t.type || t.transaction_type || '').toString();
                  const sourceKey = String(t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '');
                  const targetKey = String(t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '');
                  if (type === 'transfer') {
                    return sourceKey === branchKey || targetKey === branchKey;
                  }
                  return sourceKey === branchKey;
                })
                .slice(0, 50)
                .map((t, idx) => {
                  const type = (t.type || t.transaction_type || '').toString();
                  const branchKey = String(details.branch.id);
                  const sourceKey = String(t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '');
                  const targetKey = String(t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '');
                  const cashNeeded = computeOrderCashNeeded(t);
                  const rawAmount = Number(t.amount || 0) || 0;
                  const baseAmount = type === 'sale'
                    ? rawAmount
                    : (Number.isFinite(cashNeeded) && cashNeeded !== 0 ? cashNeeded : rawAmount);
                  const typeLabel = type === 'sale' ? 'Venta' : type === 'transfer' ? 'Transferencia interna' : 'Pedido';
                  const directionLabel = (() => {
                    if (type !== 'transfer') return null;
                    if (sourceKey === branchKey && targetKey === branchKey) return 'Interna';
                    if (sourceKey === branchKey) return 'Enviada';
                    if (targetKey === branchKey) return 'Recibida';
                    return null;
                  })();
                  return (
                    <div key={`${t.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {typeLabel}
                          {directionLabel && (
                            <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-full">
                              {directionLabel}
                            </span>
                          )}
                          {' • '}
                          {t.date || 'Sin fecha'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-300">{t.date || ''}</div>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-200">Monto: ${baseAmount.toLocaleString('en-US')}</div>
                      {type === 'transfer' && (
                        <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                          {sourceKey ? `Origen: ${getBranchNameById(sourceKey)}` : ''}
                          {sourceKey && targetKey ? ' • ' : ''}
                          {targetKey ? `Destino: ${getBranchNameById(targetKey)}` : ''}
                        </div>
                      )}
                    </div>
                  );
                })}
              {((Array.isArray(transactions) ? transactions : []).filter((t) => {
                const branchKey = String(details.branch.id);
                const type = (t.type || t.transaction_type || '').toString();
                const sourceKey = String(t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '');
                const targetKey = String(t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '');
                if (type === 'transfer') {
                  return sourceKey === branchKey || targetKey === branchKey;
                }
                return sourceKey === branchKey;
              }).length === 0) && (
                <div className="text-sm text-gray-500 dark:text-gray-400">Sin actividad registrada para esta sucursal.</div>
              )}
              <div className="pt-2 flex justify-end">
                <button onClick={() => setDetails({ show: false, branch: null })} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar sucursal */}
      {editModal?.show && editModal.branch && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md relative neon-border">
            <button onClick={() => setEditModal({ show: false, branch: null })} className="absolute top-3 right-3 p-2 rounded hover:bg-gray-100 hover:dark:bg-gray-700">
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Editar Sucursal</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Nombre</label>
                <input name="name" value={editModal.branch.name || ''} onChange={handleEditChange} required className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Dirección</label>
                <input name="address" value={editModal.branch.address || ''} onChange={handleEditChange} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Efectivo inicial</label>
                <input type="number" name="cashBalance" value={editModal.branch.cashBalance ?? 0} onChange={handleEditChange} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditModal({ show: false, branch: null })} className="border px-3 py-2 rounded border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700">Cancelar</button>
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer modal removed */}
    </div>
  );
};

export default Branches;



// Módulo de operaciones/transacciones
